import { Button } from '@ibsys/kit';
import { ChevronLeftIcon } from 'lucide-react';
import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { cn } from '@/shared/lib';
import { PageContainer, useMainLayout } from '@/shared/ui';
import { ExpertReportTree } from './ExpertReportTree/ExpertReportTree';
import { ExpertReportCard } from './ExpertReportCard/ExpertReportCard';
import { BlockCard } from './BlockCard/BlockCard';
import { KmGroupCard } from './KmGroupCard/KmGroupCard';
import { KmAssessmentCard } from './KmAssessmentCard/KmAssessmentCard';
import type { KmTreeNodeData } from './KmStructureTree/kmTreeUtils';
import { useBreakpoints } from '@/shared/hooks';

export const ExpertReportPage: FC = () => {
  const { xl: isDestop } = useBreakpoints();

  const { reportId, type, itemId } = useParams<{
    reportId?: string;
    type?: string;
    itemId?: string;
  }>();

  const { t } = useTranslation(['common', 'expertReport']);
  const navigate = useNavigate();

  const {
    breadcrumbs: { addBreadcrumbs },
  } = useMainLayout();

  useEffect(() => {
    addBreadcrumbs({
      path: '/expert-report',
      label: t('expertReport:title'),
    });
  }, [addBreadcrumbs, t]);

  const handleSelectReport = (id: number) => {
    navigate(`/expert-report/${id}/report`);
  };

  const handleSelectBlock = (reportId: number, blockId: number) => {
    navigate(`/expert-report/${reportId}/block/${blockId}`);
  };

  const handleSelectKmNode = (node: KmTreeNodeData) => {
    navigate(`/expert-report/${node.reportId}/${node.type}/${node.entityId}`);
  };

  const handleKmItemDeleted = () => {
    if (reportId) {
      navigate(`/expert-report/${reportId}/report`);
    } else {
      navigate('/expert-report');
    }
  };

  const handleReportDeleted = () => {
    navigate('/expert-report');
  };

  const handleBlockDeleted = () => {
    if (reportId) {
      navigate(`/expert-report/${reportId}/report`);
    } else {
      navigate('/expert-report');
    }
  };

  const handleBack = () => {
    navigate('/expert-report');
  };

  return (
    <PageContainer
      enableScroll={false}
      className="flex flex-col h-full xl:grid xl:grid-cols-[minmax(200px,400px)_1fr] grid-rows-1 xl:gap-5"
    >
      {/* Left: Tree — скрывается при выбранном элементе, если не десктоп */}
      <div className={cn('h-full', type ? 'hidden xl:block' : 'block')}>
        <ExpertReportTree
          selectedReportId={reportId ? +reportId : undefined}
          selectedType={type}
          selectedBlockId={type === 'block' && itemId ? +itemId : undefined}
          selectedKmItemId={(type === 'group' || type === 'assessment') && itemId ? +itemId : undefined}
          onSelectReport={handleSelectReport}
          onSelectBlock={handleSelectBlock}
          onSelectKmNode={handleSelectKmNode}
          isDesktop={isDestop}
        />
      </div>

      {/* Right: Card — скрывается при отсутствии выбранного элемента, если не десктоп */}
      <div className={cn('flex flex-col h-full', !type ? 'hidden xl:flex' : 'flex')}>
        {/* Кнопка "К списку" — только когда не десктоп и есть выбранный элемент */}
        {type && !isDestop && (
          <Button
            variant="ghost"
            size="sm"
            className="mb-3 self-start -ml-2"
            icon={<ChevronLeftIcon className="size-4" />}
            onClick={handleBack}
          >
            {t('common:backToList')}
          </Button>
        )}

        <div className="h-full min-h-0">
          {type === 'report' && reportId && (
            <ExpertReportCard reportId={+reportId} onDeleted={handleReportDeleted} className={'w-full h-full'} />
          )}

          {/* Ключ по выбранному элементу: форма карточки собирается заново, а не донашивает
              список правок прошлого элемента, пока в неё подставляются значения нового */}
          {type === 'block' && itemId && reportId && (
            <BlockCard
              key={itemId}
              reportId={+reportId}
              blockId={+itemId}
              onBlockDeleted={handleBlockDeleted}
              className={'w-full h-full'}
            />
          )}

          {type === 'group' && itemId && (
            <KmGroupCard key={itemId} groupId={+itemId} onDeleted={handleKmItemDeleted} className={'w-full h-full'} />
          )}

          {type === 'assessment' && itemId && (
            <KmAssessmentCard
              key={itemId}
              assessmentId={+itemId}
              onDeleted={handleKmItemDeleted}
              className={'w-full h-full'}
            />
          )}

          {!type && (
            <div className="flex items-center justify-center h-full text-muted-foreground">
              {t('expertReport:selectItem')}
            </div>
          )}
        </div>
      </div>
    </PageContainer>
  );
};
